import { formatClock, ROUND_SECONDS, type UsefulWindow } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { ClockRange } from "./ClockRange.tsx";

export interface WindowDraft {
  from: number;
  to: number;
  label: string;
}

/** "1:45" → 105. Round clock only: 0:00 to 1:55. */
export function parseClock(value: string): number | null {
  const m = /^\s*(\d):([0-5]\d)\s*$/.exec(value);
  if (!m) return null;
  const s = Number(m[1]) * 60 + Number(m[2]);
  return s <= 115 ? s : null;
}

export const windowDrafts = (windows: UsefulWindow[] | undefined): WindowDraft[] =>
  windows?.map((w) => ({ from: w.from, to: w.to, label: w.label })) ?? [];

/** The drafts as windows. */
export function parseWindows(drafts: WindowDraft[]): UsefulWindow[] {
  return drafts.map((w) => ({ from: w.from, to: w.to, label: w.label.trim() }));
}

/** A range on the round clock and a label per useful window. */
export function WindowsEditor({ windows, onChange }: { windows: WindowDraft[]; onChange: (w: WindowDraft[]) => void }) {
  const { t } = useTranslation();
  const set = (i: number, change: Partial<WindowDraft>) => onChange(windows.map((x, j) => (j === i ? { ...x, ...change } : x)));
  return (
    <>
      {windows.map((w, i) => (
        <div key={i} className="window-row">
          <ClockRange
            max={ROUND_SECONDS}
            from={w.from}
            to={w.to}
            format={formatClock}
            labelFrom={t("form.windowFrom", { n: i + 1 })}
            labelTo={t("form.windowTo", { n: i + 1 })}
            onChange={(from, to) => set(i, { from, to })}
          />
          <div className="window-row-foot">
            <label className="field window-label">
              <span className="visually-hidden">{t("form.label")}</span>
              <input value={w.label} maxLength={40} placeholder={t("form.windowLabelPlaceholder")} aria-label={t("form.windowLabel", { n: i + 1 })} onChange={(e) => set(i, { label: e.target.value })} />
            </label>
            <button type="button" className="btn btn-quiet btn-small" onClick={() => onChange(windows.filter((_, j) => j !== i))}>
              {t("form.removeWindow")}
            </button>
          </div>
        </div>
      ))}
      {windows.length < 6 && (
        <button type="button" className="btn btn-secondary btn-small" onClick={() => onChange([...windows, { from: 105, to: 90, label: "" }])}>
          {t("form.addWindow")}
        </button>
      )}
    </>
  );
}
